import React, { useEffect, useMemo, useState } from 'react';
import { Gift, Loader2, Plus, Trash2 } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabase';
import {
  dollarsToCents,
  pointsForEligible,
  tierProblem,
  tiersHaveGap,
  type RewardTier,
} from '../../lib/rewards';
import toast from 'react-hot-toast';

type TierDraft = { id: string; min: string; max: string; rate: string };
type RewardKind = 'fixed_discount' | 'percent_discount' | 'wallet_credit';
type RewardDraft = {
  id: string;
  name: string;
  points: string;
  kind: RewardKind;
  value: string;
  active: boolean;
};

type Activity = {
  id: string;
  points: number;
  reason: string;
  note: string | null;
  created_at: string;
  customers: { first_name: string | null; last_name: string | null } | null;
};

const KIND_LABEL: Record<RewardKind, string> = {
  fixed_discount: 'Dollar discount',
  percent_discount: 'Percent off',
  wallet_credit: 'Wallet credit',
};

function blankTier(): TierDraft {
  return { id: crypto.randomUUID(), min: '', max: '', rate: '' };
}

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function parseDollars(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return null;
  return value;
}

function draftsToTiers(drafts: TierDraft[]): { tiers: RewardTier[]; error: string | null } {
  const tiers: RewardTier[] = [];
  for (const draft of drafts) {
    const min = parseDollars(draft.min);
    const max = parseDollars(draft.max);
    const rate = parseDollars(draft.rate);
    if (min == null && max == null && rate == null) continue;
    if (rate == null || rate <= 0) return { tiers: [], error: 'Each tier needs points per dollar greater than zero.' };
    if (min != null && min < 0) return { tiers: [], error: 'A tier cannot start below $0.' };
    if (max != null && min != null && max < min) return { tiers: [], error: 'A tier’s end has to be at least its start.' };
    tiers.push({
      minCents: dollarsToCents(min ?? 0),
      maxCents: max == null ? null : dollarsToCents(max),
      pointsPerDollar: rate,
    });
  }
  return { tiers, error: tierProblem(tiers) };
}

export default function RewardsProgram() {
  const { selectedBusiness } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [programId, setProgramId] = useState<string | null>(null);
  const [active, setActive] = useState(false);
  const [expireMonths, setExpireMonths] = useState('');
  const [tiers, setTiers] = useState<TierDraft[]>([blankTier()]);
  const [rewards, setRewards] = useState<RewardDraft[]>([]);
  const [previewAmount, setPreviewAmount] = useState('350');
  const [activity, setActivity] = useState<Activity[]>([]);

  useEffect(() => {
    const load = async () => {
      if (!selectedBusiness?.id || !supabase) return;
      setLoading(true);
      try {
        const { data: program, error } = await supabase
          .from('reward_programs')
          .select('*')
          .eq('business_id', selectedBusiness.id)
          .maybeSingle();
        if (error) throw error;

        if (!program) {
          setProgramId(null);
          setActive(false);
          setExpireMonths('');
          setTiers([blankTier()]);
          setRewards([]);
        } else {
          setProgramId(program.id);
          setActive(program.is_active === true);
          setExpireMonths(program.points_expire_months ? String(program.points_expire_months) : '');
          const [{ data: tierRows }, { data: rewardRows }] = await Promise.all([
            supabase.from('reward_tiers').select('*').eq('program_id', program.id).order('min_cents'),
            supabase.from('reward_redemptions').select('*').eq('program_id', program.id).order('sort_order'),
          ]);
          setTiers(
            (tierRows || []).length
              ? (tierRows || []).map((row) => ({
                  id: row.id,
                  min: (row.min_cents / 100).toString(),
                  max: row.max_cents == null ? '' : (row.max_cents / 100).toString(),
                  rate: String(Number(row.points_per_dollar)),
                }))
              : [blankTier()]
          );
          setRewards(
            (rewardRows || []).map((row) => ({
              id: row.id,
              name: row.name || '',
              points: String(row.points_cost),
              kind: row.kind as RewardKind,
              value: row.kind === 'percent_discount'
                ? String(row.percent_off ?? '')
                : ((row.value_cents || 0) / 100).toString(),
              active: row.is_active !== false,
            }))
          );
        }

        const { data: entries } = await supabase
          .from('reward_point_entries')
          .select('id, points, reason, note, created_at, customers(first_name, last_name)')
          .eq('business_id', selectedBusiness.id)
          .order('created_at', { ascending: false })
          .limit(8);
        setActivity((entries || []).map((entry) => {
          const person = entry.customers as Activity['customers'] | Activity['customers'][] | null;
          return {
            ...entry,
            customers: Array.isArray(person) ? person[0] || null : person,
          };
        }) as Activity[]);
      } catch (err) {
        console.error('Error loading rewards program:', err);
        toast.error('Could not load the rewards program');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedBusiness?.id]);

  const parsed = useMemo(() => draftsToTiers(tiers), [tiers]);
  const previewCents = dollarsToCents(parseDollars(previewAmount) ?? 0);
  const previewPoints = parsed.error ? null : pointsForEligible(parsed.tiers, previewCents);
  const gap = !parsed.error && tiersHaveGap(parsed.tiers);

  const updateTier = (id: string, patch: Partial<TierDraft>) => {
    setTiers((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const updateReward = (id: string, patch: Partial<RewardDraft>) => {
    setRewards((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const handleSave = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    if (parsed.error && (active || parsed.tiers.length > 0 || tiers.some((t) => t.min || t.max || t.rate))) {
      toast.error(parsed.error);
      return;
    }
    if (active && parsed.tiers.length === 0) {
      toast.error('Add at least one tier before turning the program on.');
      return;
    }

    const months = expireMonths.trim();
    let expire: number | null = null;
    if (months) {
      expire = Number(months);
      if (!Number.isInteger(expire) || expire < 1 || expire > 120) {
        toast.error('Point expiry has to be between 1 and 120 months, or left blank.');
        return;
      }
    }

    const rewardRows: Array<{
      id: string;
      name: string;
      points_cost: number;
      kind: RewardKind;
      value_cents: number | null;
      percent_off: number | null;
      is_active: boolean;
    }> = [];
    for (const reward of rewards) {
      const empty = !reward.name.trim() && !reward.points.trim() && !reward.value.trim();
      if (empty) continue;
      const points = Number(reward.points);
      const value = Number(reward.value);
      if (!reward.name.trim()) {
        toast.error('Each reward needs a name.');
        return;
      }
      if (!Number.isInteger(points) || points <= 0) {
        toast.error(`“${reward.name.trim()}” needs a whole number of points.`);
        return;
      }
      if (!Number.isFinite(value) || value <= 0) {
        toast.error(`“${reward.name.trim()}” needs a reward amount.`);
        return;
      }
      if (reward.kind === 'percent_discount' && value > 100) {
        toast.error(`“${reward.name.trim()}” cannot be more than 100% off.`);
        return;
      }
      rewardRows.push({
        id: reward.id,
        name: reward.name.trim(),
        points_cost: points,
        kind: reward.kind,
        value_cents: reward.kind === 'percent_discount' ? null : dollarsToCents(value),
        percent_off: reward.kind === 'percent_discount' ? value : null,
        is_active: reward.active,
      });
    }

    try {
      setSaving(true);
      const { data: program, error: programError } = await supabase
        .from('reward_programs')
        .upsert({
          ...(programId ? { id: programId } : {}),
          business_id: selectedBusiness.id,
          is_active: active,
          points_expire_months: expire,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'business_id' })
        .select('id')
        .single();
      if (programError) throw programError;
      const id = program.id as string;
      setProgramId(id);

      const keptDrafts = tiers.filter((draft) => {
        const min = parseDollars(draft.min);
        const max = parseDollars(draft.max);
        const rate = parseDollars(draft.rate);
        return !(min == null && max == null && rate == null);
      });
      const tierWrites = keptDrafts
        .map((draft) => {
          const min = parseDollars(draft.min);
          const max = parseDollars(draft.max);
          const rate = parseDollars(draft.rate)!;
          return {
            id: draft.id,
            program_id: id,
            business_id: selectedBusiness.id,
            min_cents: dollarsToCents(min ?? 0),
            max_cents: max == null ? null : dollarsToCents(max),
            points_per_dollar: rate,
          };
        })
        .sort((a, b) => a.min_cents - b.min_cents)
        .map((row, index) => ({ ...row, sort_order: index }));

      if (tierWrites.length) {
        const { error } = await supabase.from('reward_tiers').upsert(tierWrites);
        if (error) throw error;
      }
      const keepTierIds = tierWrites.map((row) => row.id);
      let tierDelete = supabase.from('reward_tiers').delete().eq('program_id', id);
      if (keepTierIds.length) tierDelete = tierDelete.not('id', 'in', `(${keepTierIds.join(',')})`);
      const { error: tierDeleteError } = await tierDelete;
      if (tierDeleteError) throw tierDeleteError;

      const rewardWrites = rewardRows.map((row, index) => ({
        ...row,
        program_id: id,
        business_id: selectedBusiness.id,
        sort_order: index,
        updated_at: new Date().toISOString(),
      }));
      if (rewardWrites.length) {
        const { error } = await supabase.from('reward_redemptions').upsert(rewardWrites);
        if (error) throw error;
      }
      const keepRewardIds = rewardWrites.map((row) => row.id);
      let rewardDelete = supabase.from('reward_redemptions').delete().eq('program_id', id);
      if (keepRewardIds.length) rewardDelete = rewardDelete.not('id', 'in', `(${keepRewardIds.join(',')})`);
      const { error: rewardDeleteError } = await rewardDelete;
      if (rewardDeleteError) throw rewardDeleteError;

      toast.success('Rewards program saved');
    } catch (err) {
      console.error('Error saving rewards program:', err);
      const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Failed to save the rewards program';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 space-y-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Rewards Program</h2>
            <p className="text-gray-600 max-w-2xl">
              Points come from the invoice total after discounts and before tax. The whole invoice uses one tier, and the points are added when that invoice is paid in full. A refund adjusts them. Customers will see and redeem points in the portal later.
            </p>
          </div>
          <Gift className="h-8 w-8 text-gray-300 shrink-0" />
        </div>

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
          <div>
            <label className="text-sm font-semibold text-gray-900">Program status</label>
            <p className="text-sm text-gray-600 mt-1">
              {active ? 'Paid invoices earn points.' : 'Points are not being awarded.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setActive((on) => !on)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${active ? 'bg-blue-600' : 'bg-gray-300'}`}
            aria-pressed={active}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${active ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Earn tiers</h3>
            <p className="text-sm text-gray-600 mt-1">
              Set the invoice total that qualifies, and how many points each dollar in that range earns. One tier is enough. For example, $100 and up earns 1 point per dollar.
            </p>
          </div>
          <div className="space-y-3">
            {tiers.map((tier, index) => (
              <div key={tier.id} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end p-4 border border-gray-200 rounded-xl">
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 mb-1">From</span>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={tier.min}
                      onChange={(e) => updateTier(tier.id, { min: e.target.value })}
                      placeholder="0"
                      className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 mb-1">Up to</span>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={tier.max}
                      onChange={(e) => updateTier(tier.id, { max: e.target.value })}
                      placeholder="No limit"
                      className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 mb-1">Points per dollar</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={tier.rate}
                    onChange={(e) => updateTier(tier.id, { rate: e.target.value })}
                    placeholder="1"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => setTiers((rows) => rows.length === 1 ? [blankTier()] : rows.filter((row) => row.id !== tier.id))}
                  className="h-11 w-11 inline-flex items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100"
                  aria-label={`Remove tier ${index + 1}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setTiers((rows) => [...rows, blankTier()])}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-800"
          >
            <Plus className="h-4 w-4" />
            Add tier
          </button>
          {parsed.error && tiers.some((tier) => tier.min || tier.max || tier.rate) && (
            <p className="text-sm text-red-600">{parsed.error}</p>
          )}
          {gap && (
            <p className="text-sm text-amber-700">There’s a gap between tiers. An invoice that lands in the gap earns no points.</p>
          )}
          <div className="flex flex-wrap items-end gap-3 p-4 bg-gray-50 rounded-xl">
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Try an invoice total</span>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={previewAmount}
                  onChange={(e) => setPreviewAmount(e.target.value)}
                  className="w-36 pl-7 pr-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </label>
            <p className="text-sm text-gray-800 pb-2.5">
              {!tiers.some((tier) => tier.min || tier.max || tier.rate)
                ? 'Add a tier to preview an invoice.'
                : previewPoints == null
                  ? 'Fix the tiers to see what this invoice would earn.'
                  : `A ${money(previewCents)} invoice earns ${previewPoints.toLocaleString()} ${previewPoints === 1 ? 'point' : 'points'}.`}
            </p>
          </div>
        </section>

        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Rewards</h3>
            <p className="text-sm text-gray-600 mt-1">
              What points can be redeemed for. Customers will use these in the portal. A dollar discount, a percent off, or credit on their wallet.
            </p>
          </div>
          {rewards.length === 0 && (
            <p className="text-sm text-gray-500">No rewards yet.</p>
          )}
          <div className="space-y-3">
            {rewards.map((reward) => (
              <div key={reward.id} className="grid grid-cols-1 sm:grid-cols-[1.4fr_0.8fr_0.9fr_0.8fr_auto_auto] gap-3 items-end p-4 border border-gray-200 rounded-xl">
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 mb-1">Name</span>
                  <input
                    type="text"
                    value={reward.name}
                    onChange={(e) => updateReward(reward.id, { name: e.target.value })}
                    placeholder="$10 off"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 mb-1">Points</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={reward.points}
                    onChange={(e) => updateReward(reward.id, { points: e.target.value })}
                    placeholder="500"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 mb-1">Type</span>
                  <select
                    value={reward.kind}
                    onChange={(e) => updateReward(reward.id, { kind: e.target.value as RewardKind })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {(Object.keys(KIND_LABEL) as RewardKind[]).map((kind) => (
                      <option key={kind} value={kind}>{KIND_LABEL[kind]}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 mb-1">
                    {reward.kind === 'percent_discount' ? 'Percent off' : 'Amount'}
                  </span>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                      {reward.kind === 'percent_discount' ? '%' : '$'}
                    </span>
                    <input
                      type="number"
                      min="0"
                      step={reward.kind === 'percent_discount' ? '1' : '0.01'}
                      value={reward.value}
                      onChange={(e) => updateReward(reward.id, { value: e.target.value })}
                      className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </label>
                <label className="flex items-center gap-2 h-11 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={reward.active}
                    onChange={(e) => updateReward(reward.id, { active: e.target.checked })}
                  />
                  On
                </label>
                <button
                  type="button"
                  onClick={() => setRewards((rows) => rows.filter((row) => row.id !== reward.id))}
                  className="h-11 w-11 inline-flex items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100"
                  aria-label="Remove reward"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setRewards((rows) => [...rows, {
              id: crypto.randomUUID(),
              name: '',
              points: '',
              kind: 'fixed_discount',
              value: '',
              active: true,
            }])}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-800"
          >
            <Plus className="h-4 w-4" />
            Add reward
          </button>
        </section>

        <label className="block max-w-xs">
          <span className="block text-sm font-semibold text-gray-900 mb-1">Points expire after</span>
          <p className="text-sm text-gray-600 mb-2">Leave blank and points never expire. Expiry starts when customers can redeem.</p>
          <div className="relative">
            <input
              type="number"
              min="1"
              max="120"
              step="1"
              value={expireMonths}
              onChange={(e) => setExpireMonths(e.target.value)}
              placeholder="Never"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">months</span>
          </div>
        </label>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-xl"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </button>
        </div>

        {activity.length > 0 && (
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Recent points</h3>
            <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
              {activity.map((entry) => {
                const name = [entry.customers?.first_name, entry.customers?.last_name].filter(Boolean).join(' ') || 'Customer';
                const label = entry.reason === 'refund' ? 'Refund adjustment' : entry.reason === 'adjust' ? 'Manual adjustment' : 'Earned';
                return (
                  <div key={entry.id} className="flex items-center justify-between px-4 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{entry.note || label}</p>
                      <p className="text-xs text-gray-500">{name} · {label}</p>
                    </div>
                    <span className={`text-sm font-bold ${entry.points < 0 ? 'text-red-600' : 'text-green-700'}`}>
                      {entry.points > 0 ? '+' : ''}{entry.points.toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
